import { useMemo } from 'react';

import { URLS, createJurisdictionHeaders } from '@/data';
import { GetEventsQueryParams } from '@/feature/league-hub-sbk/hooks/use-events';
import { EventsWithMarketsQueryParams } from '@/feature/league-hub-sbk/hooks/use-events-with-markets';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useEventDetailsCache } from '@/hooks/use-sbk-event-details-cache';
import { cacheControlHeader } from '@/utils/constants-platform-specific';
import { QueryClient, UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { Event } from '../types';

export const PICKS_USE_EVENT_STALE_TIME = 60 * 1000;

export const getEvent = async (eventId: string): Promise<Event> => {
    let url = `${URLS.CHAMELON_API_URL}/events/${eventId}`;
    if (eventId === '1') {
        // only event 1 should pass in test=true
        url = url + '?test=true';
    }
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            ...cacheControlHeader,
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }

    useEventDetailsCache
        .getState()
        .actions.populate([{ ...(data?.data?.event_details ?? {}), id: data?.data.id, status: data?.data.status }]);

    return data.data;
};

export const createEventKeys = (product: Product) => {
    const all = [product, 'event'] as const;
    const list = [...all, 'list'] as const;
    const withMarkets = [...all, 'with-markets'] as const;
    const byId = (eventId: string) => [...all, { eventId }] as const;
    return {
        all,
        featured: [...all, 'featured'] as const,
        list,
        withMarkets,
        byLeagueWithMarkets: (params: EventsWithMarketsQueryParams) => [...withMarkets, { ...params }] as const,
        byLeague: (params: GetEventsQueryParams) => [...list, { ...params }] as const,
        byId,
        marketCategories: (eventId: string, marketCategoryId: string) =>
            [...byId(eventId), 'market-categories', marketCategoryId] as const,
    };
};

export type EventKeys = ReturnType<typeof createEventKeys>;

export const useEventKeys = (): EventKeys => {
    const product = useJurisdictionStore(state => state.product);
    return useMemo(() => createEventKeys(product), [product]);
};

export const getEventKeys = (): EventKeys => createEventKeys(useJurisdictionStore.getState().product);

// Get placeholder data from cache
function getPlaceholderData(queryClient: QueryClient, eventKeys: EventKeys, eventId: string) {
    const queriesData = queryClient.getQueriesData<{ data: Event[] }>({ queryKey: eventKeys.list });
    if (!queriesData) {
        return;
    }

    for (const data of queriesData) {
        const event = data[1]?.data.find((e: Event) => e.id === eventId);
        if (event) {
            return {
                ...event,
                market_categories: [],
            };
        }
    }
}

type EventQueryOptions = Omit<UseQueryOptions<Event>, 'queryKey' | 'queryFn' | 'placeholderData' | 'select'>;

export const useEvent = (eventId: string, queryOptions?: EventQueryOptions) => {
    const queryClient = useQueryClient();
    const eventKeys = useEventKeys();
    const enabled = queryOptions?.enabled ?? true;
    return useQuery({
        queryKey: eventKeys.byId(eventId),
        queryFn: () => getEvent(eventId),
        placeholderData: () => getPlaceholderData(queryClient, eventKeys, eventId),
        enabled: enabled && eventId !== '',
        ...queryOptions,
    });
};
